import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { api } from "../utils/api";
import { COLORS } from "../utils/colors";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import { isAdministratorPortal } from "./XeroIntegrationHealth";

function formatValue(value) {
  return value && String(value).trim() ? String(value) : "—";
}

function currentUserEmail() {
  try {
    const currentUser = JSON.parse(sessionStorage.getItem("currentUser") || "{}");
    return currentUser?.email || null;
  } catch {
    return null;
  }
}

async function acknowledgeCurrentDetails(merchantId) {
  return api(`v1/merchant/${merchantId}/xero-contact`, "POST", {
    action: "acknowledge",
    userEmail: currentUserEmail(),
  });
}

export function XeroContactMismatchAlert({
  check,
  onFix,
  merchantId,
  onAccepted,
  loading = false,
}) {
  if (!isAdministratorPortal()) return null;

  if (loading) {
    return (
      <Alert severity="info" sx={{ alignItems: "center" }}>
        <span className="flex items-center gap-2">
          <CircularProgress size={16} />
          Checking Xero contact…
        </span>
      </Alert>
    );
  }
  if (!check || check.matches || check.skipped || check.reason === "SKIP_CHECK") return null;

  const xeroUnavailable = check.reason === "XERO_UNAVAILABLE";
  const rateLimited = /rate limit/i.test(check.message || "");
  const title = xeroUnavailable
    ? rateLimited
      ? "Xero rate limit reached"
      : "Could not check the Xero contact"
    : "Xero contact id or name does not match";

  return (
    <Alert severity="info" sx={{ alignItems: "flex-start" }}>
      <p className="font-semibold mb-1">{title}</p>
      <p className="text-sm mb-2">
        {check.message ||
          "The linked Xero contact id does not match this merchant name. This does not stop verification."}
      </p>
      {Array.isArray(check.mismatches) && check.mismatches.length > 0 && (
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left border-b border-amber-300/60">
                <th className="py-1 pr-3 font-semibold">Field</th>
                <th className="py-1 pr-3 font-semibold">Priority One</th>
                <th className="py-1 font-semibold">Xero</th>
              </tr>
            </thead>
            <tbody>
              {check.mismatches.map((row) => (
                <tr key={row.field} className="border-b border-amber-300/40 align-top">
                  <td className="py-1 pr-3 font-medium whitespace-nowrap">{row.label}</td>
                  <td className="py-1 pr-3 break-all">{formatValue(row.merchant)}</td>
                  <td className="py-1 break-all">{formatValue(row.xero)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!xeroUnavailable && (
        <div className="flex flex-wrap gap-2 mt-3">
          {onFix && (
            <Button
              variant="contained"
              size="small"
              onClick={onFix}
              sx={{ backgroundColor: COLORS.priorityBlue.hex }}
            >
              Create or match
            </Button>
          )}
          {merchantId && (
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={async () => {
                try {
                  const response = await acknowledgeCurrentDetails(merchantId);
                  if (!response?.data?.success) {
                    showErrorToast(response?.data?.message || "Could not mark these details as correct.");
                    return;
                  }
                  showSuccessToast(response.data.message || "Xero contact check skipped for this merchant.");
                  onAccepted?.(response.data.data?.check, response.data.data?.merchant);
                } catch (error) {
                  showErrorToast(
                    error?.response?.data?.message || "Could not mark these details as correct."
                  );
                }
              }}
            >
              Mark current details as correct
            </Button>
          )}
        </div>
      )}
    </Alert>
  );
}

export function XeroContactResolveDialog({
  open,
  onClose,
  merchantId,
  check,
  onResolved,
}) {
  const adminPortal = isAdministratorPortal();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    if (!open || !adminPortal) {
      setQuery("");
      setResults([]);
      setSuggestions([]);
      setSearchError("");
      setSaving(false);
    }
  }, [open, adminPortal]);

  useEffect(() => {
    if (!open || !adminPortal || !merchantId) return undefined;
    let cancelled = false;
    (async () => {
      setSearching(true);
      setSearchError("");
      try {
        const response = await api(`v1/merchant/${merchantId}/xero-contact?suggest=true`, "GET");
        if (cancelled) return;
        if (response?.data?.success) {
          setSuggestions(response.data.data?.suggestions || []);
        } else {
          setSearchError(response?.data?.message || "Could not load suggested Xero contacts.");
        }
      } catch (error) {
        if (!cancelled) {
          setSearchError(error?.response?.data?.message || "Could not load suggested Xero contacts.");
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, adminPortal, merchantId]);

  useEffect(() => {
    if (!open || !adminPortal) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearchError("");
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      setSearchError("");
      try {
        const response = await api(
          `v1/merchant/xero-contacts/search?q=${encodeURIComponent(term)}`,
          "GET"
        );
        if (response?.data?.success) {
          setResults(response.data.data?.contacts || []);
        } else {
          setResults([]);
          setSearchError(response?.data?.message || "Could not search Xero contacts.");
        }
      } catch (error) {
        setResults([]);
        setSearchError(error?.response?.data?.message || "Could not search Xero contacts.");
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [open, adminPortal, query]);

  const handleCreate = async () => {
    if (!merchantId || saving) return;
    setSaving(true);
    try {
      const response = await api(`v1/merchant/${merchantId}/xero-contact`, "POST", {
        action: "create",
      });
      if (!response?.data?.success) {
        showErrorToast(response?.data?.message || "Could not create the Xero contact.");
        return;
      }
      showSuccessToast(response.data.message || "Xero contact created.");
      onResolved?.(response.data.data?.check, response.data.data?.merchant);
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Could not create the Xero contact.");
    } finally {
      setSaving(false);
    }
  };

  const handleLink = async (contactId) => {
    if (!merchantId || !contactId || saving) return;
    setSaving(true);
    try {
      const response = await api(`v1/merchant/${merchantId}/xero-contact`, "POST", {
        action: "link",
        contactId,
      });
      if (!response?.data?.success) {
        showErrorToast(response?.data?.message || "Could not link that Xero contact.");
        return;
      }
      showSuccessToast(response.data.message || "Xero contact linked.");
      onResolved?.(response.data.data?.check, response.data.data?.merchant);
    } catch (error) {
      showErrorToast(error?.response?.data?.message || "Could not link that Xero contact.");
    } finally {
      setSaving(false);
    }
  };

  if (!adminPortal) return null;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Match Xero contact</DialogTitle>
      <DialogContent className="flex flex-col gap-4">
        <p className="text-sm text-gray-700">
          Bills use the Xero contact stored on this merchant. Create a contact from the Priority
          One details, or search and link the correct existing contact.
        </p>
        {check?.merchant && (
          <div className="text-sm bg-gray-50 border border-gray-200 rounded p-3 space-y-1">
            <p>
              <strong>Priority One:</strong> {formatValue(check.merchant.name)}
            </p>
            <p>{formatValue(check.merchant.email)}</p>
            <p>
              {[check.merchant.street, check.merchant.city, check.merchant.state, check.merchant.postcode]
                .filter(Boolean)
                .join(", ") || "—"}
            </p>
          </div>
        )}
        {merchantId && (
          <Button
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                const response = await acknowledgeCurrentDetails(merchantId);
                if (!response?.data?.success) {
                  showErrorToast(response?.data?.message || "Could not mark these details as correct.");
                  return;
                }
                showSuccessToast(response.data.message || "Xero contact check skipped for this merchant.");
                onResolved?.(response.data.data?.check, response.data.data?.merchant);
              } catch (error) {
                showErrorToast(
                  error?.response?.data?.message || "Could not mark these details as correct."
                );
              } finally {
                setSaving(false);
              }
            }}
          >
            Mark current details as correct
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ backgroundColor: COLORS.priorityBlue.hex, alignSelf: "flex-start" }}
          disabled={saving}
          onClick={handleCreate}
        >
          {saving ? "Saving…" : "Create Xero contact from merchant details"}
        </Button>
        <TextField
          fullWidth
          size="small"
          label="Search existing Xero contacts"
          placeholder="Name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          helperText="Suggested matches load automatically. Type to search further."
        />
        {searching && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <CircularProgress size={16} />
            {query.trim().length >= 2 ? "Searching…" : "Loading suggested matches…"}
          </div>
        )}
        {searchError && <p className="text-sm text-amber-700">{searchError}</p>}
        {!searching && query.trim().length >= 2 && results.length === 0 && !searchError && (
          <p className="text-sm text-gray-600">No Xero contacts matched that search.</p>
        )}
        {!searching && query.trim().length < 2 && suggestions.length === 0 && !searchError && (
          <p className="text-sm text-gray-600">No likely Xero matches found. Search by name or email.</p>
        )}
        {(query.trim().length >= 2 ? results : suggestions).length > 0 && (
          <div>
            <p className="text-sm font-semibold mb-2">
              {query.trim().length >= 2 ? "Search results" : "Suggested matches"}
            </p>
            <div className="border border-gray-200 rounded divide-y max-h-64 overflow-auto">
              {(query.trim().length >= 2 ? results : suggestions).map((contact) => (
                <div key={contact.contactId} className="flex items-start justify-between gap-3 p-3">
                  <div className="text-sm">
                    <p className="font-semibold">{formatValue(contact.name)}</p>
                    <p>{formatValue(contact.email)}</p>
                    <p className="text-gray-600">{formatValue(contact.city)}</p>
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={saving}
                    onClick={() => handleLink(contact.contactId)}
                  >
                    Link
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
